import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export function GlowOrb({ className }: { className?: string }) {
  return (
    <div className={`absolute rounded-full blur-[120px] pointer-events-none ${className}`} />
  );
}


export default function NavBar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [progress, setProgress] = useState(0);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      setScrolled(currentY > 40);
      setHidden(currentY > lastScrollY.current && currentY > 200);
      lastScrollY.current = currentY;

      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (currentY / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.2, rootMargin: '-80px 0px -60% 0px' }
    );

    const sections = ['产品', '架构', '场景', '案例', '博客', '关于'];
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const navItems = ['产品', '架构', '场景', '案例', '博客', '关于'];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-950/90 backdrop-blur-xl border-b border-background-800/50' : 'bg-transparent'
      } ${hidden ? '-translate-y-full' : 'translate-y-0'}`}
    >
      <div
        className="absolute top-0 left-0 h-[2px] bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-150"
        style={{ width: `${progress}%` }}
      />

      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className={`flex items-center gap-2.5 transition-transform duration-300 ${scrolled ? 'scale-95' : 'scale-100'}`}>
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/20">
            <i className="ri-robot-2-line text-lg text-primary-400" />
          </span>
          <span className="font-heading text-lg font-bold text-foreground-100 tracking-tight">0xman</span>
          <span className="text-[10px] font-mono text-primary-400 bg-primary-500/10 px-1.5 py-0.5 rounded">HUB</span>
        </div>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => {
            const isActive = activeSection === item;
            return (
              <a
                key={item}
                href={`#${item}`}
                className={`relative text-sm transition-colors whitespace-nowrap ${
                  isActive ? 'text-primary-400' : 'text-foreground-400 hover:text-foreground-200'
                }`}
              >
                {item}
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-400 rounded-full" />
                )}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <button
              onClick={() => navigate('/console')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-400 text-background-950 text-sm font-medium transition-all cursor-pointer whitespace-nowrap btn-lift"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-dashboard-line" />
              </span>
              进入控制台
            </button>
          ) : (
            /* 这是舰队管理后台, 不是面向 C 端的产品:没有自助注册。
               身份由 hub 的令牌决定(admin 一把 / 每设备一把), 只有"登录"这一条路。 */
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-400 text-background-950 text-sm font-medium transition-all cursor-pointer whitespace-nowrap btn-lift"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shield-user-line" />
              </span>
              登录控制台
            </button>
          )}
        </div>
      </div>
    </header>
  );
}